import React, { useState } from 'react';
import LegalLinks from '@/components/LegalLinks';
import { useTheme } from '@/contexts/ThemeContext';

const s = {
  label: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '18px',
    fontWeight: 400,
    color: 'rgba(255, 243, 225, 0.85)',
    margin: 0,
    lineHeight: 1.3
  },
  desc: {
    fontSize: '14px',
    fontWeight: 400,
    color: 'rgba(255, 243, 225, 0.35)',
    margin: '4px 0 0 0',
    lineHeight: 1.4
  },
  sectionHeader: {
    fontFamily: "'EB Garamond', Georgia, serif",
    fontSize: '12px',
    fontWeight: 400,
    letterSpacing: '0.15em',
    textTransform: 'uppercase',
    color: 'rgba(196, 137, 74, 0.50)',
    margin: '0 0 8px 0'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: '64px',
    padding: '12px 0',
    borderBottom: '1px solid rgba(255, 243, 225, 0.06)'
  }
};

const Toggle = ({ on, onToggle, testId }) => (
  <button
    onClick={onToggle}
    data-testid={testId}
    style={{
      width: '52px',
      height: '30px',
      borderRadius: '100px',
      border: 'none',
      background: on ? 'rgba(196, 137, 74, 0.70)' : 'rgba(255, 243, 225, 0.12)',
      cursor: 'pointer',
      position: 'relative',
      flexShrink: 0,
      transition: 'background 0.25s ease',
      WebkitTapHighlightColor: 'transparent'
    }}
  >
    <div style={{
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      background: '#fff',
      position: 'absolute',
      top: '3px',
      left: on ? '25px' : '3px',
      transition: 'left 0.25s ease'
    }} />
  </button>
);

const SelectField = ({ testId, children }) => (
  <select
    data-testid={testId}
    style={{
      width: '100%',
      background: 'rgba(255, 243, 225, 0.04)',
      border: '1px solid rgba(255, 243, 225, 0.12)',
      borderRadius: '12px',
      padding: '12px 14px',
      fontSize: '16px',
      color: 'rgba(255, 243, 225, 0.75)',
      outline: 'none',
      WebkitAppearance: 'none',
      appearance: 'none'
    }}
  >
    {children}
  </select>
);

const SettingsView = ({ onBack }) => {
  const { theme, autoMode, setTheme, toggleAutoMode } = useTheme();
  const [showWelcome, setShowWelcome] = useState(
    localStorage.getItem('eunoia_hide_welcome') !== 'true'
  );
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const themes = [
    { value: 'day',             label: 'Day',             desc: 'Warm parchment with honey accents' },
    { value: 'night',           label: 'Night',           desc: 'Deep charcoal with dusk blues' },
    { value: 'neurodivergent',  label: 'Neurodivergent',  desc: 'High contrast, no animations' },
    { value: 'sigma',           label: 'Sigma',           desc: 'Dark-first, clean geometry' },
  ];

  return (
    <div
      data-testid="settings-view"
      style={{
        position: 'fixed',
        inset: 0,
        background: '#080608',
        backgroundImage: 'radial-gradient(ellipse at 50% 30%, rgba(62, 28, 18, 0.3) 0%, rgba(8, 6, 8, 0) 70%)',
        zIndex: 50,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch'
      }}
    >
      {/* Texture */}
      <div style={{
        position: 'fixed', inset: 0, opacity: 0.035, pointerEvents: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.75' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
      }} />

      <div style={{ position: 'relative', zIndex: 2, padding: '0 32px 80px 32px', maxWidth: '520px', margin: '0 auto' }}>

        {/* Back button */}
        <button
          onClick={onBack}
          data-testid="back-btn"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '24px 0',
            minHeight: '48px',
            display: 'flex',
            alignItems: 'center',
            fontFamily: "'EB Garamond', Georgia, serif",
            fontSize: '15px',
            fontWeight: 400,
            letterSpacing: '0.10em',
            textTransform: 'uppercase',
            color: 'rgba(255, 243, 225, 0.45)',
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          ← back
        </button>

        {/* ─── THEME ─── */}
        <div style={{ marginTop: '16px' }}>
          <p style={s.sectionHeader}>appearance</p>

          {themes.map((t) => (
            <button
              key={t.value}
              onClick={() => setTheme(t.value)}
              data-testid={`theme-${t.value}`}
              style={{
                ...s.row,
                width: '100%',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid rgba(255, 243, 225, 0.06)',
                cursor: 'pointer',
                textAlign: 'left',
                WebkitTapHighlightColor: 'transparent'
              }}
            >
              <div style={{ flex: 1, marginRight: '16px' }}>
                <p style={s.label}>{t.label}</p>
                <p style={s.desc}>{t.desc}</p>
              </div>
              <div style={{
                width: '18px',
                height: '18px',
                borderRadius: '50%',
                border: theme === t.value
                  ? '2px solid rgba(196, 137, 74, 0.70)'
                  : '1.5px solid rgba(255, 243, 225, 0.20)',
                background: theme === t.value
                  ? 'rgba(196, 137, 74, 0.70)'
                  : 'transparent',
                flexShrink: 0,
                transition: 'all 0.2s ease'
              }} />
            </button>
          ))}
        </div>

        {/* ─── VOICE REPLIES ─── */}
        <div style={{ marginTop: '40px' }}>
          <p style={s.sectionHeader}>voice</p>

          <div style={s.row}>
            <div style={{ flex: 1, marginRight: '16px' }}>
              <p style={s.label}>Voice replies</p>
              <p style={s.desc}>Speak responses aloud</p>
            </div>
            <Toggle on={false} onToggle={() => {}} testId="voice-replies-toggle" />
          </div>
        </div>

        {/* ─── SHOW WELCOME ─── */}
        <div style={{ marginTop: '40px' }}>
          <p style={s.sectionHeader}>general</p>

          <div style={s.row}>
            <div style={{ flex: 1, marginRight: '16px' }}>
              <p style={s.label}>Show welcome message</p>
              <p style={s.desc}>Display the welcome message after each login</p>
            </div>
            <Toggle
              on={showWelcome}
              onToggle={() => {
                const next = !showWelcome;
                setShowWelcome(next);
                if (next) {
                  localStorage.removeItem('eunoia_hide_welcome');
                } else {
                  localStorage.setItem('eunoia_hide_welcome', 'true');
                }
              }}
              testId="welcome-toggle"
            />
          </div>
        </div>

        {/* ─── ADVANCED (collapsed) ─── */}
        <div style={{ marginTop: '40px' }}>
          <button
            onClick={() => setAdvancedOpen(o => !o)}
            data-testid="advanced-toggle"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              background: 'transparent',
              border: 'none',
              borderBottom: advancedOpen ? 'none' : '1px solid rgba(255, 243, 225, 0.06)',
              cursor: 'pointer',
              padding: '12px 0',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            <span style={{
              fontFamily: "'EB Garamond', Georgia, serif",
              fontSize: '18px',
              color: 'rgba(255, 243, 225, 0.50)',
            }}>
              Advanced settings
            </span>
            <svg
              width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="rgba(255,243,225,0.35)" strokeWidth="1.5"
              strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: advancedOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.25s ease', flexShrink: 0 }}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>

          {advancedOpen && (
            <div data-testid="advanced-section">

              {/* Follow sunrise & sunset */}
              <div style={s.row}>
                <div style={{ flex: 1, marginRight: '16px' }}>
                  <p style={s.label}>Follow sunrise & sunset</p>
                  <p style={s.desc}>Auto-switch between Day and Night</p>
                </div>
                <Toggle on={autoMode} onToggle={toggleAutoMode} testId="auto-mode-toggle" />
              </div>

              {/* Response pace */}
              <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch' }}>
                <p style={{ ...s.label, marginBottom: '10px' }}>Response pace</p>
                <SelectField testId="response-pace-select">
                  <option value="slow">Slow (more pauses)</option>
                  <option value="medium" defaultValue>Medium</option>
                  <option value="conversational">Conversational</option>
                </SelectField>
              </div>

              {/* Depth preference */}
              <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch' }}>
                <p style={{ ...s.label, marginBottom: '10px' }}>Depth preference</p>
                <SelectField testId="depth-select">
                  <option value="light">Light</option>
                  <option value="moderate" defaultValue>Moderate</option>
                  <option value="deep">Deep</option>
                </SelectField>
              </div>

              {/* Voice tone */}
              <div style={{ ...s.row, flexDirection: 'column', alignItems: 'stretch' }}>
                <p style={{ ...s.label, marginBottom: '10px' }}>Voice tone</p>
                <SelectField testId="voice-tone-select">
                  <option value="grounding">Grounding</option>
                  <option value="tender" defaultValue>Tender</option>
                  <option value="light">Light</option>
                </SelectField>
              </div>

              {/* Hands-free note */}
              <div style={{ ...s.row, flexDirection: 'column', alignItems: 'flex-start' }}>
                <p style={{ ...s.desc, fontSize: '13px', lineHeight: 1.6, color: 'rgba(255, 243, 225, 0.25)' }}>
                  For the best hands free experience, keep your screen active during voice conversations.
                </p>
              </div>

              {/* Privacy */}
              <div style={{ ...s.row, flexDirection: 'column', alignItems: 'flex-start' }}>
                <p style={{ ...s.desc, fontSize: '15px', lineHeight: 1.6 }}>
                  Your conversations are stored securely. We never sell your data or use it for advertising.
                </p>
                <LegalLinks style={{ marginTop: '10px', justifyContent: 'flex-start', gap: '16px' }} />
              </div>

              {/* Confidentiality agreement */}
              {localStorage.getItem('beta_nda_accepted') === 'true' && (
                <div style={{ ...s.row, borderBottom: 'none', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <p style={s.sectionHeader}>confidentiality agreement</p>
                  <p style={{ ...s.label, marginBottom: '6px' }}>Accepted</p>
                  <p style={{ ...s.desc, fontSize: '14px', lineHeight: 1.6 }}>
                    {localStorage.getItem('beta_name') || 'Beta participant'} accepted the Eunoia Presence Beta Confidentiality Agreement.
                  </p>
                  <p style={{ ...s.desc, fontSize: '13px', marginTop: '4px', color: 'rgba(255, 243, 225, 0.25)' }}>
                    This agreement remains in effect until the beta period concludes.
                  </p>
                </div>
              )}

            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default SettingsView;
